import { z } from 'zod';

/**
 * Schema für die Erstellung eines Event-Feedbacks.
 * Wird für POST /public/events/:id/feedback genutzt.
 *
 * @example
 * ```ts
 * const result = CreateEventFeedbackSchema.safeParse(body);
 * ```
 */
export const CreateEventFeedbackSchema = z
  .object({
    /** Sterne-Bewertung von 1 (schlecht) bis 5 (sehr gut) */
    rating: z.number().int().min(1).max(5),
    /** Optionaler Freitext-Kommentar */
    comment: z.string().max(2000).optional(),
    /** War der Fan beim Event anwesend? */
    attended: z.boolean(),
    /** Begründung bei Nicht-Anwesenheit — Pflicht wenn attended = false */
    reason: z.string().min(10).max(500).optional(),
  })
  .refine((data) => data.attended || (data.reason !== undefined && data.reason.length >= 10), {
    message: 'Begründung ist erforderlich wenn du nicht anwesend warst (min. 10 Zeichen)',
    path: ['reason'],
  });

/** TypeScript Type für Event-Feedback-Erstellung */
export type CreateEventFeedback = z.infer<typeof CreateEventFeedbackSchema>;

/**
 * Anonymisierter Feedback-Response für Team-Rolle.
 * Enthält keine Nutzer-Identifikationsdaten.
 */
export const EventFeedbackSchema = z.object({
  id: z.string(),
  eventId: z.string(),
  rating: z.number().int().min(1).max(5),
  comment: z.string().nullable().optional(),
  attended: z.boolean(),
  reason: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
});

/** TypeScript Type für anonymisierten Feedback-Response */
export type EventFeedback = z.infer<typeof EventFeedbackSchema>;

/**
 * Feedback-Response mit Nutzerdaten für Admin-Rolle.
 * Enthält Namen und E-Mail des Feedback-Gebers.
 */
export const EventFeedbackAdminSchema = EventFeedbackSchema.extend({
  userId: z.string(),
  user: z.object({
    firstName: z.string().nullable().optional(),
    lastName: z.string().nullable().optional(),
    email: z.string().email(),
  }),
});

/** TypeScript Type für Admin-Feedback-Response (mit Nutzerdaten) */
export type EventFeedbackAdmin = z.infer<typeof EventFeedbackAdminSchema>;

/**
 * Zusammenfassung aller Feedbacks eines Events.
 * Wird als Wrapper-Response zurückgegeben.
 */
export const EventFeedbackSummarySchema = z.object({
  /** Gesamtanzahl der eingereichten Feedbacks */
  count: z.number().int().nonnegative(),
  /** Durchschnittliche Sterne-Bewertung (gerundet auf 1 Dezimalstelle) */
  averageRating: z.number().nullable(),
  /** Anzahl der Anwesenden */
  attendedCount: z.number().int().nonnegative(),
  /** Feedback-Einträge (anonymisiert oder mit Nutzerdaten je nach Rolle) */
  items: z.array(EventFeedbackSchema),
});

/** TypeScript Type für Event-Feedback-Zusammenfassung */
export type EventFeedbackSummary = z.infer<typeof EventFeedbackSummarySchema>;

/**
 * Admin-Zusammenfassung aller Feedbacks eines Events.
 * Enthält Nutzerdaten in den Einträgen.
 */
export const EventFeedbackAdminSummarySchema = EventFeedbackSummarySchema.extend({
  items: z.array(EventFeedbackAdminSchema),
});

/** TypeScript Type für Admin-Feedback-Zusammenfassung */
export type EventFeedbackAdminSummary = z.infer<typeof EventFeedbackAdminSummarySchema>;
